import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type QuotePayload } from './api';
import { addLine, dropLine, setLineQty, setLinePrice, type NewLine, type PriceChange } from './cart';

/** Délai après le dernier clic sur + / − avant d'envoyer la quantité : une rafale = un appel. */
const QTY_DELAY_MS = 400;

/** `seq` : numéro d'envoi, dans l'ordre où les envois partent (et sont traités). */
type Send = { path: string; method: 'POST' | 'PATCH' | 'DELETE'; body?: object; seq?: number };

/**
 * Modifications du panier d'une commande, affichées tout de suite.
 *
 * - Les envois partent un à la fois, dans l'ordre (`scope`), même après avoir
 *   quitté la page.
 * - La réponse du serveur ne remplace l'écran qu'une fois TOUT envoyé et confirmé :
 *   elle contient alors chaque changement, avec les prix recalculés.
 * - Une relecture de la commande (`read`) commencée avant un changement affiché
 *   n'écrase jamais ce changement.
 * - Un refus affiche le message et relit la commande.
 */
export function useCartEdits(quoteId: number) {
  const qc = useQueryClient();
  const key = ['quote', quoteId];
  const inflight = useRef(0);
  const timers = useRef(new Map<number, { timer: ReturnType<typeof setTimeout>; qty: number }>());
  const tempId = useRef(0);
  /** Augmente à chaque changement affiché : une relecture plus ancienne est périmée. */
  const epoch = useRef(0);
  const sent = useRef(0);
  /** Numéro du dernier envoi parti quand le dernier refus est arrivé. */
  const errorAfter = useRef(0);
  /** Lignes changées à l'écran, pas encore confirmées par le serveur. */
  const [touched, setTouched] = useState<ReadonlySet<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const idle = () => inflight.current === 0 && timers.current.size === 0;

  const mutation = useMutation({
    scope: { id: `quote-edit-${quoteId}` },
    mutationFn: ({ path, method, body }: Send) =>
      api<QuotePayload>(path, { method, body: body ? JSON.stringify(body) : undefined }),
    onSuccess: (data, vars) => {
      inflight.current--;
      // Le message d'un refus reste jusqu'à la confirmation d'un changement fait APRÈS
      // lui, pas d'un changement déjà en route (deux scans de suite, le 1er refusé).
      if ((vars.seq ?? 0) > errorAfter.current) setError(null);
      if (!idle()) return;
      const before = qc.getQueryData<QuotePayload>(key);
      void qc.cancelQueries({ queryKey: key });
      qc.setQueryData(key, data);
      setTouched(new Set());
      // La clé des coûtants (updatedAt, à la seconde) n'a pas toujours changé : relus quand même.
      if (before?.quote.updatedAt === data.quote.updatedAt) {
        void qc.invalidateQueries({ queryKey: ['quote-costs', quoteId] });
      }
    },
    onError: (err) => {
      inflight.current--;
      errorAfter.current = sent.current;
      setError((err as Error).message);
      if (!idle()) return;
      void qc.cancelQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: key }).then(() => {
        if (idle()) setTouched(new Set());
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['quotes'] });
    },
  });

  /**
   * Lecture de la commande pour la requête de la page : si un changement a été
   * affiché pendant la lecture, ou attend sa confirmation, l'écran est gardé.
   */
  async function read(fetch: () => Promise<QuotePayload>): Promise<QuotePayload> {
    const startedAt = epoch.current;
    const fresh = await fetch();
    const shown = qc.getQueryData<QuotePayload>(key);
    return shown && (epoch.current !== startedAt || !idle()) ? shown : fresh;
  }

  function show(update: (d: QuotePayload) => QuotePayload, lineId: number) {
    epoch.current++;
    void qc.cancelQueries({ queryKey: key });
    qc.setQueryData<QuotePayload>(key, (d) => (d ? update(d) : d));
    setTouched((s) => new Set(s).add(lineId));
  }

  function send(s: Send) {
    inflight.current++;
    mutation.mutate({ ...s, seq: ++sent.current });
  }

  const linePath = (lineId: number) => `/api/quotes/${quoteId}/lines/${lineId}`;

  /** Envoie tout de suite la quantité qui attendait la fin de la rafale de clics. */
  function flushQty(lineId: number) {
    const pending = timers.current.get(lineId);
    if (!pending) return;
    clearTimeout(pending.timer);
    timers.current.delete(lineId);
    send({ path: linePath(lineId), method: 'PATCH', body: { qty: pending.qty } });
  }

  function changeQty(lineId: number, qty: number) {
    show((d) => setLineQty(d, lineId, qty), lineId);
    const pending = timers.current.get(lineId);
    if (pending) clearTimeout(pending.timer);
    const timer = setTimeout(() => flushQty(lineId), QTY_DELAY_MS);
    timers.current.set(lineId, { timer, qty });
  }

  function removeLine(lineId: number) {
    const pending = timers.current.get(lineId);
    if (pending) {
      clearTimeout(pending.timer);
      timers.current.delete(lineId);
    }
    show((d) => dropLine(d, lineId), lineId);
    send({ path: linePath(lineId), method: 'DELETE' });
  }

  function setPrice(lineId: number, change: PriceChange) {
    // La quantité en attente part d'abord : le serveur tarife la ligne à la bonne quantité.
    flushQty(lineId);
    show((d) => setLinePrice(d, lineId, change), lineId);
    send({ path: linePath(lineId), method: 'PATCH', body: change });
  }

  /** `body` : ce que le serveur attend ; `line` : ce que l'écran montre en attendant. */
  function add(body: object, line: NewLine) {
    const id = --tempId.current;
    const existing = qc
      .getQueryData<QuotePayload>(key)
      ?.lines.find((l) => l.partNum === line.partNum && l.mfgCode === line.mfgCode);
    // Le serveur additionne à SA quantité : celle qui attend doit lui parvenir avant.
    if (existing) flushQty(existing.id);
    show((d) => addLine(d, line, id), existing?.id ?? id);
    send({ path: `/api/quotes/${quoteId}/lines`, method: 'POST', body });
  }

  // En quittant la page, une quantité pas encore envoyée part quand même, dans l'ordre.
  // Onglet masqué (tablette mise en veille, autre app) : envoyée tout de suite, la page
  // vit encore. Onglet fermé sans avoir été masqué : envoi direct (keepalive), en
  // dernier recours. Page revenue du cache du navigateur : l'écran est relu.
  useEffect(() => {
    const pending = timers.current;
    const flushAll = () => {
      for (const lineId of [...pending.keys()]) flushQty(lineId);
    };
    const onHidden = () => document.visibilityState === 'hidden' && flushAll();
    const onPageHide = () => {
      for (const [lineId, { timer, qty }] of pending) {
        clearTimeout(timer);
        void api(linePath(lineId), { method: 'PATCH', body: JSON.stringify({ qty }), keepalive: true }).catch(() => {});
      }
      pending.clear();
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted || !idle()) return;
      setTouched(new Set());
      void qc.invalidateQueries({ queryKey: key });
    };
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      flushAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  return { changeQty, removeLine, setPrice, add, read, touched, error };
}

export type CartEdits = ReturnType<typeof useCartEdits>;
